import { useState, useMemo, useCallback } from 'react'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import { Box, Typography, Dialog } from '@mui/material'
import EmptyState from '../../components/Shared/EmptyState'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import { useTranslation } from 'react-i18next'
import { TableSearch } from '../../components/Table/TableSearch'
import { DownloadCsvButton } from '../../components/Table/DownloadCsvButton'
import { FilterPill } from '../../components/Table/FilterPill'
import { BulkActionsBar } from '../../components/Table/BulkActionsBar'
import {
  IQueryUsersData,
  IQueryUsersVars,
  QUERY_USERS_LIST,
} from '../../graphql/users/queryUsersList'
import {
  IGetTenantParkingPoolUsersVars,
  IGetTenantParkingPoolUsersData,
  GET_TENANT_PARKING_POOL_USERS,
} from '../../graphql/parkingPool/getTenantParkingPoolUsers'
import {
  BULK_ASSIGN_PARKING_POOL_PROFILE,
  IBulkAssignParkingPoolProfileData,
  IBulkAssignParkingPoolProfileVars,
} from '../../graphql/parkingPool/bulkAssignParkingPoolProfile'
import {
  BULK_UNASSIGN_FROM_PARKING_POOL,
  IBulkUnassignFromParkingPoolData,
  IBulkUnassignFromParkingPoolVars,
} from '../../graphql/parkingPool/bulkUnassignFromParkingPool'
import {
  ASSIGN_USER_TO_PARKING_POOL,
  IAssignUserToParkingPoolData,
  IAssignUserToParkingPoolVars,
} from '../../graphql/parkingPool/assignUserToParkingPool'
import {
  SET_PARKING_POOL_USER_DATA,
  ISetParkingPoolUserDataData,
  ISetParkingPoolUserDataVars,
} from '../../graphql/parkingPool/setParkingPoolUserData'
import {
  QUERY_USER,
  IQueryUserData,
  IQueryUserVars,
} from '../../graphql/users/queryUser'
import { IQueryBasicData } from '../../graphql/IQueryData'
import { ParkingSubscriptionStatus } from '../../graphql/tenant/getTenant'
import FullScreenLoader from '../../components/Shared/FullScreenLoader'
import { useAppSelector } from '../../redux/store'
import BaseDrawer from '../../components/Shared/BaseDrawer'
import UserDetails from '../../components/ParkingUserDetails/UserDetails'
import ReserveOnBehalfModal from '../../components/ParkingUserDetails/ReserveOnBehalfModal'
import { ProfileLimitGrid } from '../../components/ParkingUserDetails/ProfileLegend'
import { IUserDataRow } from '../../components/Parking/ParkingPoolUsersTable'
import ParkingPoolUsersTable from '../../components/Parking/ParkingPoolUsersTable'
import NoPlacesAssignedAlert from '../../components/Parking/NoPlacesAssignedAlert'
import { useSnackbar } from '../../components/Shared/useSnackbar'
import { colors } from '../../const/colors'
import { downloadCsv as exportCsv } from '../../utils/downloadCsv'

const UsersList = () => {
  const { t } = useTranslation(['parking_pool_users', 'generic'])
  const { tenantName } = useAppSelector(state => state.user)

  const [openedUser, setOpenedUser] = useState<IUserDataRow>()
  const [reserveUser, setReserveUser] = useState<IUserDataRow>()
  const [search, setSearch] = useState('')
  const [assignedFilter, setAssignedFilter] = useState('')
  const [profileFilter, setProfileFilter] = useState('')
  const [selectedRows, setSelectedRows] = useState<IUserDataRow[]>([])
  const [tableKey, setTableKey] = useState(0)
  const [profileDialog, setProfileDialog] = useState<'profile' | 'service'>()
  const { notify, notifyError } = useSnackbar()

  const [loadReserveUser, { data: reserveUserData }] = useLazyQuery<
    IQueryUserData,
    IQueryUserVars
  >(QUERY_USER, { fetchPolicy: 'no-cache' })

  const [bulkAssignProfile] = useMutation<
    IBulkAssignParkingPoolProfileData,
    IBulkAssignParkingPoolProfileVars
  >(BULK_ASSIGN_PARKING_POOL_PROFILE, { onError: notifyError })

  const [bulkUnassign] = useMutation<
    IBulkUnassignFromParkingPoolData,
    IBulkUnassignFromParkingPoolVars
  >(BULK_UNASSIGN_FROM_PARKING_POOL, { onError: notifyError })

  const [assignProfileQuietly] = useMutation<
    IBulkAssignParkingPoolProfileData,
    IBulkAssignParkingPoolProfileVars
  >(BULK_ASSIGN_PARKING_POOL_PROFILE)

  const [assignToPool] = useMutation<
    IAssignUserToParkingPoolData,
    IAssignUserToParkingPoolVars
  >(ASSIGN_USER_TO_PARKING_POOL)

  const [setUserData] = useMutation<
    ISetParkingPoolUserDataData,
    ISetParkingPoolUserDataVars
  >(SET_PARKING_POOL_USER_DATA)
  const { data, loading, refetch } = useQuery<IQueryUsersData, IQueryUsersVars>(
    QUERY_USERS_LIST,
    { fetchPolicy: 'no-cache', variables: { tenant: tenantName } },
  )

  const {
    data: poolData,
    refetch: refetchPool,
    loading: poolLoading,
  } = useQuery<IGetTenantParkingPoolUsersData, IGetTenantParkingPoolUsersVars>(
    GET_TENANT_PARKING_POOL_USERS,
    { fetchPolicy: 'no-cache', variables: { tenant: tenantName } },
  )

  const tenant = data?.getTenant ?? undefined
  const parkingPool = poolData?.getTenant.parkingPool ?? undefined

  const assignedLogins = useMemo(
    () => new Set(parkingPool?.users?.map(user => user.login) ?? []),
    [parkingPool],
  )

  const profiles = useMemo(() => parkingPool?.profiles ?? [], [parkingPool])

  const filteredUsers = useMemo(() => {
    if (!tenant) return []
    let items = tenant.employees

    if (search.trim()) {
      const searchQuery = search.trim().toLowerCase()
      items = items.filter(user => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
        const email = (
          user.workEmail?.email ||
          user.email?.email ||
          ''
        ).toLowerCase()
        return (
          fullName.includes(searchQuery) ||
          user.login.toLowerCase().includes(searchQuery) ||
          email.includes(searchQuery)
        )
      })
    }

    if (assignedFilter === 'yes') {
      items = items.filter(user => assignedLogins.has(user.login))
    } else if (assignedFilter === 'no') {
      items = items.filter(user => !assignedLogins.has(user.login))
    }

    if (profileFilter) {
      items = items.filter(
        user => user.parkingPoolProfile?.name === profileFilter,
      )
    }

    return items
  }, [tenant, search, assignedFilter, profileFilter, assignedLogins])

  const openReserve = (row: IUserDataRow) => {
    setReserveUser(row)
    loadReserveUser({ variables: { login: row.login } })
  }

  const bookedRanges = useMemo(() => {
    const user = reserveUserData?.queryUser[0]
    if (!user || user.login !== reserveUser?.login) return []
    return (user.parkingSubscriptions ?? [])
      .filter(
        sub =>
          sub.validFrom &&
          sub.validTo &&
          sub.status !== ParkingSubscriptionStatus.cancelled,
      )
      .map(sub => ({
        from: sub.validFrom.slice(0, 10),
        to: sub.validTo.slice(0, 10),
      }))
  }, [reserveUserData, reserveUser])

  const clearSelection = () => {
    setSelectedRows([])
    setTableKey(prevKey => prevKey + 1)
  }

  const clearFilters = () => {
    setSearch('')
    setAssignedFilter('')
    setProfileFilter('')
  }

  const hasActiveFilters = !!search || !!assignedFilter || !!profileFilter

  const bulkProfile = () => {
    if (!profiles.length) {
      notify(t('no_profiles_available'))
      return
    }
    setProfileDialog('profile')
  }

  const selectedLogins = () =>
    selectedRows.filter(row => row.isAssigned).map(row => row.login)

  const assignedSelectedCount = selectedRows.filter(
    row => row.isAssigned,
  ).length

  const assignableLogins = selectedRows
    .filter(row => !row.isAssigned && row.isReady)
    .map(row => row.login)

  const bulkService = () => {
    if (!profiles.length) {
      notify(t('no_profiles_available'))
      return
    }
    if (profiles.length === 1) {
      bulkAssignToService(profiles[0].id)
      return
    }
    setProfileDialog('service')
  }

  const withReason = (summary: string, reasons: string[]) => {
    const unique = Array.from(new Set(reasons.filter(Boolean)))
    return unique.length === 1
      ? `${summary} ${t('toast_failure_reason', { reason: unique[0] })}`
      : summary
  }

  const bulkAssignToService = async (profileId: string) => {
    if (!parkingPool?.id) return
    const logins = assignableLogins
    setProfileDialog(undefined)
    clearSelection()

    const enrolled: string[] = []
    const reasons: string[] = []
    const failWith = (payload?: IQueryBasicData | null, error?: Error) => {
      reasons.push(error?.message || payload?.message || payload?.error || '')
    }

    for (const login of logins) {
      let error: Error | undefined
      const assignRes = await assignToPool({
        variables: { pool: parkingPool.id, login },
      }).catch((e: Error) => {
        error = e
        return undefined
      })
      const assignPayload = assignRes?.data?.assignUserToParkingPool
      if (!assignPayload?.status) {
        failWith(assignPayload, error)
        continue
      }
      const enableRes = await setUserData({
        variables: { login, enabled: true },
      }).catch((e: Error) => {
        error = e
        return undefined
      })
      const enablePayload = enableRes?.data?.setParkingPoolUserData
      if (!enablePayload?.status) {
        failWith(enablePayload, error)
        continue
      }
      enrolled.push(login)
    }

    let profileFailed = 0
    if (enrolled.length) {
      let error: Error | undefined
      const res = await assignProfileQuietly({
        variables: { profile: profileId, logins: enrolled },
      }).catch((e: Error) => {
        error = e
        return undefined
      })
      const payload = res?.data?.bulkAssignParkingPoolProfile
      if (payload) {
        profileFailed = payload.failed
        payload.errors.forEach(entry => reasons.push(entry.error))
      } else {
        profileFailed = enrolled.length
        reasons.push(error?.message ?? '')
      }
    }

    const ok = enrolled.length - profileFailed
    const failed = logins.length - ok

    if (!failed) {
      notify(t('toast_service_assigned', { n: ok }))
    } else {
      notifyError(
        withReason(
          ok
            ? t('toast_service_partial', { ok, failed })
            : t('toast_service_failed', { n: failed }),
          reasons,
        ),
      )
    }
    refetchData()
  }

  const bulkAssignToProfile = async (profileId: string) => {
    const logins = selectedLogins()
    setProfileDialog(undefined)
    clearSelection()
    const res = await bulkAssignProfile({
      variables: { profile: profileId, logins },
    })
    const payload = res.data?.bulkAssignParkingPoolProfile
    if (!payload) return
    if (!payload.failed) {
      notify(t('toast_profile_assigned', { n: payload.succeeded }))
    } else {
      notifyError(
        withReason(
          payload.succeeded
            ? t('toast_profile_partial', {
                ok: payload.succeeded,
                failed: payload.failed,
              })
            : t('toast_profile_failed', { n: payload.failed }),
          payload.errors.map(entry => entry.error),
        ),
      )
    }
    refetchData()
  }

  const bulkDetach = async () => {
    if (!parkingPool?.id) return
    const logins = selectedLogins()
    clearSelection()
    const res = await bulkUnassign({
      variables: { parkingPool: parkingPool.id, logins },
    })
    const payload = res.data?.bulkUnassignFromParkingPool
    if (!payload) return
    if (!payload.failed) {
      notify(t('toast_detached', { n: payload.succeeded }))
    } else {
      notifyError(
        withReason(
          payload.succeeded
            ? t('toast_detached_partial', {
                ok: payload.succeeded,
                failed: payload.failed,
              })
            : t('toast_detached_failed', { n: payload.failed }),
          payload.errors.map(entry => entry.error),
        ),
      )
    }
    refetchData()
  }

  const refetchData = () => {
    refetch()
    refetchPool()
  }

  const downloadCsv = useCallback(() => {
    const rows = [
      [
        t('csv_full_name'),
        t('csv_login'),
        t('csv_work_email'),
        t('csv_profile'),
        t('csv_service'),
      ],
      ...filteredUsers.map(user => [
        `${user.firstName} ${user.lastName}`,
        user.login,
        user.workEmail?.email || user.email?.email || '',
        user.parkingPoolProfile?.name || '—',
        assignedLogins.has(user.login) ? t('generic:yes') : t('generic:no'),
      ]),
    ]
    exportCsv(rows, 'uzytkownicy-parking.csv')
    notify(t('toast_generating_csv'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsers, assignedLogins, t])

  if (loading || poolLoading) return <FullScreenLoader />

  if (!parkingPool) {
    return (
      <EmptyState
        icon={<PeopleOutlinedIcon sx={{ fontSize: 34, color: colors.slate }} />}
        title={t('no_parking_data')}
        description={t('no_parking_data_desc')}
      />
    )
  }

  const serviceOptions = [
    { value: '', label: t('service_all') },
    { value: 'yes', label: t('service_assigned') },
    { value: 'no', label: t('service_unassigned') },
  ]

  const profileOptions = [
    { value: '', label: t('profile_all') },
    ...profiles.map(profile => ({ value: profile.name, label: profile.name })),
  ]

  return (
    <>
      <NoPlacesAssignedAlert />

      <Box
        sx={{
          fontSize: '12px',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.green,
          fontWeight: 800,
          mb: '6px',
        }}
      >
        {t('eyebrow')}
      </Box>
      <Typography
        component="h1"
        sx={{
          fontSize: '30px',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: colors.navy,
          lineHeight: 1.15,
          mb: '4px',
        }}
      >
        {t('title')}
      </Typography>
      <Typography
        sx={{
          fontSize: '14.5px',
          color: colors.textMuted,
          mb: '24px',
        }}
      >
        {t('subtitle')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          mb: '18px',
          flexWrap: 'wrap',
        }}
      >
        <TableSearch
          value={search}
          onChange={setSearch}
          placeholder={t('search_placeholder')}
        />

        {profiles.length > 0 && (
          <FilterPill
            e2e="filter-profile"
            label={t('filter_profile')}
            value={profileFilter}
            options={profileOptions}
            onSelect={setProfileFilter}
          />
        )}

        <FilterPill
          e2e="filter-service"
          label={t('filter_service')}
          value={assignedFilter}
          options={serviceOptions}
          onSelect={setAssignedFilter}
        />

        {hasActiveFilters && (
          <Box
            onClick={clearFilters}
            data-e2e="clear-filters"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              height: 48,
              px: '14px',
              borderRadius: colors.radiusPill,
              background: colors.bg,
              fontSize: '13px',
              color: colors.slate,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              transition: 'color .15s',
              '&:hover': { color: colors.textSecondary },
            }}
          >
            {t('clear_filters')}
          </Box>
        )}

        <DownloadCsvButton onClick={downloadCsv} />
      </Box>

      <BulkActionsBar
        count={selectedRows.length}
        onClear={clearSelection}
        actions={[
          {
            label: t('assign_service_count', { n: assignableLogins.length }),
            e2e: 'bulk-assign-service',
            onClick: bulkService,
            disabled: !assignableLogins.length,
          },
          {
            label: t('assign_profile'),
            e2e: 'bulk-assign-profile',
            onClick: bulkProfile,
            disabled: !assignedSelectedCount,
          },
          {
            label: t('detach_service'),
            e2e: 'bulk-detach',
            onClick: bulkDetach,
            variant: 'danger',
            disabled: !assignedSelectedCount,
          },
        ]}
      />

      <ParkingPoolUsersTable
        key={tableKey}
        users={filteredUsers}
        tenant={tenant}
        assigned={assignedLogins}
        parkingPool={parkingPool}
        setOpenUserDetails={setOpenedUser}
        onSelectionChange={setSelectedRows}
        onReserve={openReserve}
      />

      {!!reserveUser && (
        <ReserveOnBehalfModal
          open
          onClose={() => setReserveUser(undefined)}
          login={reserveUser.login}
          parkingPoolId={parkingPool.id}
          userName={reserveUser.fullName}
          bookedRanges={bookedRanges}
          onConfirm={() => {
            setReserveUser(undefined)
            notify(t('generic:success_message'))
            refetchData()
          }}
        />
      )}

      <BaseDrawer
        open={!!openedUser}
        width={552}
        background={colors.bg}
        disablePadding
      >
        {!!openedUser && !!parkingPool?.id && (
          <UserDetails
            userData={openedUser}
            closeDrawer={() => setOpenedUser(undefined)}
            listRefetch={refetchData}
            tenantName={tenantName}
            parkingPoolId={parkingPool.id}
            profiles={profiles}
          />
        )}
      </BaseDrawer>

      <Dialog
        open={!!profileDialog}
        onClose={() => setProfileDialog(undefined)}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: colors.radiusModal,
            width: Math.min(profiles.length, 3) * 336 + 52,
            maxWidth: 'calc(100vw - 48px)',
            p: '26px',
          },
        }}
      >
        <Typography sx={{ fontSize: '19px', fontWeight: 800, mb: '6px' }}>
          {profileDialog === 'service'
            ? t('assign_service')
            : t('assign_profile')}
        </Typography>
        <Typography
          sx={{ fontSize: '13px', color: colors.textMuted, mb: '18px' }}
        >
          {profileDialog === 'service'
            ? t('select_profile_for_service', { n: assignableLogins.length })
            : t('select_profile_for_users', { n: assignedSelectedCount })}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
            gap: '14px',
            alignItems: 'stretch',
          }}
        >
          {profiles.map(profile => (
            <Box
              key={profile.id}
              data-e2e="profile-option"
              onClick={() =>
                profileDialog === 'service'
                  ? bulkAssignToService(profile.id)
                  : bulkAssignToProfile(profile.id)
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: '16px 18px',
                borderRadius: '14px',
                border: `1.5px solid ${colors.border}`,
                fontWeight: 700,
                fontSize: '14px',
                color: colors.navy,
                cursor: 'pointer',
                transition: 'border-color .12s, background .12s',
                '&:hover': {
                  borderColor: colors.navy,
                  background: colors.bg,
                },
              }}
            >
              {profile.name}
              <ProfileLimitGrid profile={profile} />
            </Box>
          ))}
        </Box>
      </Dialog>
    </>
  )
}

export default UsersList
